import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import type { ViewBody } from '@ayumia/contracts';
import { collectionInputs, isCoreRepresentation, type CoreRepresentation } from '@ayumia/component-runtime';
import { ApiError, type ApiClient } from '../../../api/client';
import { text } from '../../../app/i18n';
import { Icon } from '../../../design-system/icons';
import { Dialog, DialogActions } from '../../../design-system/Overlay';
import { PageHeader, type MenuAction } from '../../../design-system/primitives';
import { useToast } from '../../../design-system/Toast';
import { Loading } from '../../../states/States';
import { useAppServices } from '../../ViewContext';
import { bulkActionSpec, bulkAvailability } from '../bulk-actions';
import { NOT_PERMITTED, useCommandAccess } from '../command-access';
import { displayPreferenceKey, useDisplayPreference } from '../display-preference';
import { selectionKeys, useSelection, type Selection } from '../selection';
import { SelectionToolbar, type ToolbarAction } from '../SelectionToolbar';
import { ChipMenu, FolderBreadcrumb, FolderTree, NewMenu, Shortcuts, ViewSwitch, type DropTarget, type TrailStep } from './FolderChrome';
import { MoveToFolderDialog } from './FolderDialogs';
import { folderCommands, type FolderEntry, type FolderNode, type FolderSource, type Listing, type ListingRequest } from './folder-source';
import { DRAG_TYPE, FolderGrid, FolderTable, folderKey, itemKey, type ItemPresenter } from './FolderViews';
import './folders.css';

/**
 * Liste à dossiers du composant de collection (ADR-149 §1 à §5) : la vue déclare l'affichage, les
 * gestes de sélection, les actions groupées, la source de dossiers et la présentation (`drive` ou
 * `tree`) ; ce composant les rend pour **toute** liste rangée par dossier. La page n'apporte que son
 * extension déclarée (vignettes, dépôt, panneau de détails). Tout passe par des commandes : le serveur
 * autorise, valide et refuse ; un refus s'affiche, il ne se devine pas.
 */

export type InfoTarget<Item> = { kind: 'item'; item: Item } | { kind: 'folder'; folder: FolderEntry; place: string };

export interface FolderCollectionLabels {
  root: string;
  items: string;
  one: string;
  many: string;
  /** Accord des participes (« une image déposée ») : 'e' ou ''. */
  e: string;
  search: string;
  /** Libellé de l'ouverture d'un élément : « Aperçu », « Propriétés ». */
  open: string;
}

export interface FolderCollectionProps<Item> {
  view: ViewBody;
  viewKey: string;
  client: ApiClient;
  source: FolderSource<Item>;
  presenter: ItemPresenter<Item>;
  labels: FolderCollectionLabels;
  folderId: string | null;
  onNavigate: (folderId: string | null) => void;
  onNavigateWorkspace?: (ref: string) => void;
  /** Change quand la page a modifié la source (dépôt terminé…). */
  refreshKey?: number;
  /** Entrées de la page dans « Nouveau » (dépôt de fichiers). */
  newEntries?: ReadonlyArray<{ id: string; label: string; icon: string; onSelect: () => void; disabled?: boolean; reason?: string }>;
  /** Fichiers du poste lâchés sur la zone des éléments. */
  onDropFiles?: (files: File[]) => void;
  /** Lecture faite : la page en tire le dossier courant (usage du dépôt…). */
  onListing?: (listing: Listing<Item>) => void;
  /** Bandeau de la page sous les outils (dépôts en cours…). */
  notice?: ReactNode;
  /** Zone vide (dépôt), ou fin de grille. */
  empty?: (searching: boolean) => ReactNode;
  trailing?: ReactNode;
  /** Actions de la page dans la barre de sélection (télécharger…), sur les éléments choisis un à un. */
  extraActions?: (items: readonly Item[], everything: boolean) => ToolbarAction[];
  /** Contenu du panneau d'informations (« Détails »). */
  info?: (target: InfoTarget<Item>, controls: { close: () => void; refresh: () => void; openFolder: (id: string) => void; rename: (folder: FolderEntry) => void; move: (item: Item) => void }) => ReactNode;
  /** Description sous le titre de la page. */
  subtitle?: (folder: FolderEntry | null) => string | undefined;
  /** Étapes du fil d'Ariane avant la racine (Accueil, module) : la charpente commune des pages. */
  trail?: readonly TrailStep[];
}

const SEARCH_DEBOUNCE_MS = 400;
const countOf = (count: number, one: string, many: string): string => `${count.toLocaleString('fr')} ${count > 1 ? many : one}`;
const messageOf = (error: unknown): string => (error instanceof ApiError || error instanceof Error ? error.message : String(error));

export function FolderCollection<Item>(props: FolderCollectionProps<Item>) {
  const { view, client, source, presenter, labels, folderId } = props;
  const inputs = collectionInputs(view);
  const spec = inputs.folders!;
  const drive = spec.presentation === 'drive';
  const toast = useToast();
  const services = useAppServices();
  const commands = useMemo(() => folderCommands(client, spec, source), [client, spec, source]);
  // Refus visibles d'avance (addendum du 2026-10-20) : une commande déclarée que la politique n'accorde
  // pas est grisée avec son motif ; une commande non déclarée n'est pas présentée. Le serveur reste juge.
  const declaredCommands = spec.commands ?? {};
  const access = useCommandAccess(client, [declaredCommands.create?.command_name, declaredCommands.rename?.command_name, declaredCommands.move?.command_name, declaredCommands.archive?.command_name, declaredCommands.move_items?.command_name, ...(inputs.bulk_commands ?? []).map((entry) => (typeof entry === 'string' ? entry : entry.command_name))]);
  const may = {
    create: access.allowed(declaredCommands.create?.command_name),
    rename: access.allowed(declaredCommands.rename?.command_name),
    move: access.allowed(declaredCommands.move?.command_name),
    archive: access.allowed(declaredCommands.archive?.command_name),
    moveItems: access.allowed(declaredCommands.move_items?.command_name),
  };
  const denied = (allowed: boolean) => (allowed ? {} : { disabled: true, hint: NOT_PERMITTED });

  // Affichage (ADR-149 §1) : les représentations Table (icône de lignes) et CardList (icône de grille,
  // des tuiles) de la vue ; choix gardé pour la personne.
  const modes: Array<{ id: string; kind: 'table' | 'grid'; label: string; icon: string }> = view.representations.flatMap((representation): Array<{ id: string; kind: 'table' | 'grid'; label: string; icon: string }> =>
    isCoreRepresentation(representation, 'Table') ? [{ id: representation.id, kind: 'table' as const, label: text(representation.label, 'fr', 'Liste'), icon: 'rows' }] : isCoreRepresentation(representation, 'CardList') ? [{ id: representation.id, kind: 'grid' as const, label: text(representation.label, 'fr', 'Grille'), icon: 'cards' }] : [],
  );
  const preference = inputs.display?.remember === false ? null : displayPreferenceKey(props.viewKey, services.context);
  const [modeId, setModeId] = useDisplayPreference(preference, modes.map((mode) => mode.id), view.entry);
  const mode = modes.find((entry) => entry.id === modeId) ?? modes[0];
  const table = view.representations.find((representation): representation is CoreRepresentation<'Table'> => isCoreRepresentation(representation, 'Table'));
  const columns = (table?.inputs.columns ?? []).map((column) => ({ field: column.field, label: text(column.label, 'fr', column.field), ...((column as { align?: string }).align === 'end' ? { align: 'end' as const } : {}) }));

  // Requête : recherche (anti-rebond), portée, tri, puces, raccourci.
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQ(draft.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);
  const [scope, setScope] = useState<'folder' | 'everywhere'>(spec.search_scope?.default ?? 'everywhere');
  const sortOptions = (spec.listing_query?.sort_options ?? []).map((option) => ({ id: option.value, label: text(option.label) }));
  const [sort, setSort] = useState<string | undefined>(spec.listing_query?.default_sort);
  const [chips, setChips] = useState<Record<string, string>>({});
  const shortcuts = inputs.drive?.shortcuts ?? [];
  const [shortcutId, setShortcutId] = useState<string | null>(null);
  const shortcut = shortcuts.find((entry) => entry.shortcut_id === shortcutId);
  useEffect(() => setShortcutId(null), [folderId]);
  const shortcutFilters = Object.fromEntries(Object.entries(shortcut?.filter ?? {}).flatMap(([field, ops]) => (typeof ops['eq'] === 'string' ? [[field, ops['eq']]] : [])));
  const everywhere = shortcut?.everywhere === true;
  const request: ListingRequest = { folderId: everywhere ? null : folderId, q, scope: everywhere ? 'everywhere' : scope, ...(everywhere ? { all: true } : {}), filters: { ...chips, ...shortcutFilters }, ...(shortcut?.sort ?? sort) === undefined ? {} : { sort: (shortcut?.sort ?? sort)! } };
  const requestKey = JSON.stringify(request);

  // Lecture.
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((value) => value + 1), []);
  const [listing, setListing] = useState<Listing<Item> | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const onListing = useRef(props.onListing);
  onListing.current = props.onListing;
  useEffect(() => {
    let alive = true;
    setFailure(null);
    source
      .list(JSON.parse(requestKey) as ListingRequest)
      .then((read) => {
        if (!alive) return;
        setListing(read);
        onListing.current?.(read);
      })
      .catch((error: unknown) => alive && setFailure(messageOf(error)));
    return () => {
      alive = false;
    };
  }, [source, requestKey, tick, props.refreshKey]);
  // Arborescence (tree) et « Déplacer vers… » : tous les dossiers, relus après chaque changement.
  const [tree, setTree] = useState<FolderNode[] | null>(null);
  useEffect(() => {
    let alive = true;
    source.allFolders().then((found) => alive && setTree(found)).catch(() => alive && setTree([]));
    return () => {
      alive = false;
    };
  }, [source, tick, props.refreshKey]);

  const folders = listing?.folders ?? [];
  const items = listing?.items ?? [];
  const total = listing?.total ?? 0;
  const order = [...folders.map((folder) => folderKey(folder.id)), ...items.map((item) => itemKey(presenter.id(item)))];
  const base = useSelection(total, order, { range: inputs.selection?.range !== false });
  // « Sélectionner les N » couvre les éléments de l'ensemble filtré, jamais les dossiers.
  const selection: Selection = useMemo(() => ({ ...base, has: (key: string) => (base.mode === 'all' ? key.startsWith('i:') : base.ids.has(key)) }), [base]);
  const clearSelection = base.clear;
  useEffect(() => clearSelection(), [requestKey, clearSelection]);
  const pickedFolders = base.mode === 'all' ? [] : folders.filter((folder) => base.ids.has(folderKey(folder.id)));
  const pickedItems = base.mode === 'all' ? items : items.filter((item) => base.ids.has(itemKey(presenter.id(item))));

  // Panneau d'informations, dossiers en cours d'édition, boîtes de dialogue.
  const [info, setInfo] = useState<InfoTarget<Item> | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  useEffect(() => setInfo(null), [folderId]);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [moving, setMoving] = useState<{ label: string; folders: FolderEntry[]; items: 'all' | readonly Item[] } | null>(null);
  const [confirming, setConfirming] = useState<{ folder: FolderEntry; count: number } | null>(null);
  // Action destructive toujours confirmée (règle de l'utilisateur) : archiver un dossier, même vide, ou un élément.
  const [asking, setAsking] = useState<{ title: string; message: string; label: string; run: () => void } | null>(null);

  const run = async (action: () => Promise<unknown>, done?: string): Promise<boolean> => {
    try {
      await action();
      if (done !== undefined) toast('success', done);
      refresh();
      return true;
    } catch (error) {
      toast('danger', messageOf(error));
      return false;
    }
  };
  const plural = (count: number, verb: string): string => `${countOf(count, labels.one, labels.many)} ${verb}${labels.e}${count > 1 ? 's' : ''}.`;
  const itemsTarget = (chosen: 'all' | readonly Item[]) => (chosen === 'all' ? { request, total } : { ids: chosen.map((item) => presenter.id(item)) });

  const moveTo = async (job: { folders: readonly FolderEntry[]; items: 'all' | readonly Item[] }, target: string | null): Promise<void> => {
    const count = job.items === 'all' ? total : job.items.length;
    if (count > 0 && !(await run(() => commands.moveItems(itemsTarget(job.items), target), plural(count, 'déplacé')))) return;
    for (const folder of job.folders) if (!(await run(() => commands.move(folder, target), `Dossier « ${folder.name} » déplacé.`))) return;
    base.clear();
  };
  const archiveFolderNow = async (folder: FolderEntry): Promise<void> => {
    try {
      await commands.archive(folder);
      toast('success', `Dossier « ${folder.name} » archivé.`);
      refresh();
    } catch (error) {
      // Un dossier non vide ne disparaît pas sans que l'utilisateur ait vu ce qu'il contient.
      const count = error instanceof ApiError && error.code === 'MEDIA_FOLDER_NOT_EMPTY' ? Number(/(\d+) élément/.exec(error.message)?.[1]) : NaN;
      if (Number.isFinite(count)) setConfirming({ folder, count });
      else toast('danger', messageOf(error));
    }
  };

  // Glisser-déposer (ADR-149 §4) : un élément (ou la sélection dont il fait partie) sur un dossier.
  const [over, setOver] = useState<string | null | undefined>(undefined);
  const dragFrom = (key: string): { draggable?: true; onDragStart?: (event: DragEvent<HTMLElement>) => void } => (!may.moveItems ? {} : { draggable: true as const, onDragStart: (event: DragEvent<HTMLElement>) => { event.dataTransfer.setData(DRAG_TYPE, key); event.dataTransfer.effectAllowed = 'move'; } });
  const dropTarget = (target: string | null): DropTarget => ({
    'data-drop-over': over === target,
    onDragOver: (event) => {
      if (![...event.dataTransfer.types].includes(DRAG_TYPE) || !may.moveItems) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (over !== target) setOver(target);
    },
    onDragLeave: () => setOver(undefined),
    onDrop: (event) => {
      const key = event.dataTransfer.getData(DRAG_TYPE);
      setOver(undefined);
      if (key === '' || !commands.can.moveItems || !may.moveItems) return;
      event.preventDefault();
      event.stopPropagation();
      const keys = selection.has(key) && base.mode === 'ids' ? [...base.ids] : [key];
      const movedFolders = folders.filter((folder) => keys.includes(folderKey(folder.id)) && folder.id !== target && !folder.locked);
      const movedItems = items.filter((item) => keys.includes(itemKey(presenter.id(item))));
      if (movedFolders.length + movedItems.length > 0) void moveTo({ folders: movedFolders, items: movedItems }, target);
    },
  });
  // Fichiers du poste lâchés sur la zone : la page dépose.
  const [dropping, setDropping] = useState(false);
  const depth = useRef(0);
  const carries = (event: DragEvent<HTMLElement>): boolean => [...event.dataTransfer.types].includes('Files');
  const fileZone = props.onDropFiles === undefined ? {} : {
    onDragEnter: (event: DragEvent<HTMLElement>) => { if (!carries(event)) return; event.preventDefault(); depth.current += 1; setDropping(true); },
    onDragOver: (event: DragEvent<HTMLElement>) => { if (!carries(event)) return; event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; },
    onDragLeave: (event: DragEvent<HTMLElement>) => { if (!carries(event)) return; depth.current = Math.max(0, depth.current - 1); if (depth.current === 0) setDropping(false); },
    onDrop: (event: DragEvent<HTMLElement>) => { if (!carries(event)) return; event.preventDefault(); depth.current = 0; setDropping(false); if (event.dataTransfer.files.length > 0) props.onDropFiles!([...event.dataTransfer.files]); },
  };

  // Menus.
  const crumbs = listing?.path ?? [];
  const parentOf = (folder: FolderEntry): string | null => (folder.id === listing?.folder?.id ? (crumbs.length > 1 ? crumbs[crumbs.length - 2]!.id : null) : folderId);
  const placeOf = (folder: FolderEntry): string => [labels.root, ...(folder.id === listing?.folder?.id ? crumbs.slice(0, -1) : crumbs).map((crumb) => crumb.name)].join(' › ');
  const folderMenu = (folder: FolderEntry): MenuAction[] => {
    const lock = (hint: string) => (folder.locked ? { disabled: true, hint } : {});
    return [
      ...(folder.id === listing?.folder?.id ? [] : [{ id: 'open', label: 'Ouvrir', icon: 'folder', onSelect: () => props.onNavigate(folder.id) }]),
      ...(props.info === undefined ? [] : [{ id: 'properties', label: 'Propriétés', icon: 'eye', onSelect: () => { setInfo({ kind: 'folder', folder, place: placeOf(folder) }); setInfoOpen(true); } }]),
      ...(commands.can.rename ? [{ id: 'rename', label: 'Renommer', icon: 'pencil', onSelect: () => { if (folder.id === listing?.folder?.id) props.onNavigate(parentOf(folder)); else if (!folders.some((entry) => entry.id === folder.id)) props.onNavigate(typeof folder.raw['parent_id'] === 'string' ? folder.raw['parent_id'] : null); setCreating(false); setRenaming(folder.id); }, ...denied(may.rename), ...lock('Dossier système : son nom est fixé') }] : []),
      ...(commands.can.move ? [{ id: 'move', label: 'Déplacer vers…', icon: 'move', onSelect: () => setMoving({ label: folder.name, folders: [folder], items: [] }), ...denied(may.move), ...lock('Dossier système : sa place est fixée') }] : []),
      ...(commands.can.archive ? [{ id: 'archive', label: 'Archiver', icon: 'archive', tone: 'danger' as const, separator: true, onSelect: () => setAsking({ title: `Archiver le dossier « ${folder.name} » ?`, message: 'Le dossier est archivé, jamais effacé ici ; les liens vers son contenu restent valides.', label: 'Archiver', run: () => void archiveFolderNow(folder) }), ...denied(may.archive), ...lock('Dossier système : il ne s’archive pas') }] : []),
    ];
  };
  const declared = (inputs.bulk_commands ?? []).map(bulkActionSpec);
  const archiveSpec = declared.find((entry) => entry.danger && entry.command_name !== spec.commands?.move_items?.command_name);
  const openItem = (item: Item): void => {
    setInfo({ kind: 'item', item });
    setInfoOpen(true);
  };
  const itemMenu = (item: Item): MenuAction[] => [
    { id: 'open', label: labels.open, icon: 'eye', onSelect: () => openItem(item) },
    ...presenter.menu(item),
    ...(commands.can.moveItems ? [{ id: 'move', label: 'Déplacer vers…', icon: 'move', onSelect: () => setMoving({ label: presenter.title(item), folders: [], items: [item] }), ...denied(may.moveItems) }] : []),
    ...(archiveSpec === undefined ? [] : [{ id: 'archive', label: archiveSpec.label ?? 'Archiver', icon: archiveSpec.icon ?? 'archive', tone: 'danger' as const, separator: true, ...denied(access.allowed(archiveSpec.command_name)), onSelect: () => setAsking({ title: `${archiveSpec.label ?? 'Archiver'} « ${presenter.title(item)} » ?`, message: archiveSpec.confirm?.message ?? 'L’élément est archivé, jamais effacé ici ; les liens restent valides.', label: archiveSpec.label ?? 'Archiver', run: () => void run(() => commands.runItems(archiveSpec.command_name, { ids: [presenter.id(item)] }), `« ${presenter.title(item)} » archivé${labels.e}.`).then((ok) => { if (ok && info?.kind === 'item' && presenter.id(info.item) === presenter.id(item)) setInfo(null); }) }) }]),
  ];

  // Barre de sélection : actions déclarées, bornées ; « Déplacer vers… » ouvre la boîte commune.
  const everythingSelected = base.mode === 'all';
  const count = everythingSelected ? total : base.ids.size;
  const lockedPicked = pickedFolders.some((folder) => folder.locked);
  const toolbarActions: ToolbarAction[] = [
    ...(props.extraActions?.(pickedItems, everythingSelected) ?? []),
    ...declared.map((entry): ToolbarAction => {
      const available = bulkAvailability(entry, count);
      const isMove = entry.command_name === spec.commands?.move_items?.command_name;
      const foldersRefused = pickedFolders.length > 0 && entry.appliesTo === 'items' ? 'Ne s’applique pas aux dossiers.' : lockedPicked ? 'Un dossier système ne se déplace ni ne s’archive.' : undefined;
      const permitted = access.allowed(entry.command_name) && (pickedFolders.length === 0 || (isMove ? may.move : may.archive));
      const reason = permitted ? (available.reason ?? foldersRefused) : NOT_PERMITTED;
      const label = entry.label ?? (isMove ? 'Déplacer vers…' : entry.command_name);
      return {
        id: entry.command_name,
        label,
        icon: entry.icon ?? (isMove ? 'move' : entry.danger ? 'archive' : 'check'),
        danger: entry.danger,
        disabled: reason !== undefined,
        ...(reason === undefined ? {} : { reason }),
        confirm: isMove ? null : entry.confirm === null ? null : { ...(entry.confirm.title === undefined ? {} : { title: entry.confirm.title }), message: entry.confirm.message ?? `${label} : ${count.toLocaleString('fr')} élément${count > 1 ? 's' : ''}. Les liens vers ces éléments restent valides.` },
        onRun: () => {
          if (isMove) {
            setMoving({ label: countOf(count, 'élément', 'éléments'), folders: pickedFolders, items: everythingSelected ? 'all' : pickedItems });
            return;
          }
          void (async () => {
            const chosen = everythingSelected ? 'all' : pickedItems;
            const itemCount = chosen === 'all' ? total : chosen.length;
            if (itemCount > 0 && !(await run(() => commands.runItems(entry.command_name, itemsTarget(chosen)), entry.danger ? plural(itemCount, 'archivé') : `${label} : fait.`))) return;
            for (const folder of pickedFolders) await archiveFolderNow(folder);
            base.clear();
          })();
        },
      };
    }),
  ];
  const canSelectAll = source.resolvesSelection && !everythingSelected && total > items.length && pickedItems.length === items.length && items.length > 0;
  const where = listing?.folder === null || listing === null ? '' : ' du dossier';
  const selectionLabel = everythingSelected ? `${countOf(total, labels.one, labels.many)} sélectionné${labels.e}${total > 1 ? 's' : ''} (tout l’ensemble filtré)` : `${count.toLocaleString('fr')} sélectionné${count > 1 ? 's' : ''}`;

  // Rendu.
  const title = shortcut !== undefined && (shortcut.filter !== undefined || shortcut.sort !== undefined || everywhere) ? text(shortcut.label) : undefined;
  const current = listing?.folder ?? null;
  const searching = q !== '' || Object.values(chips).some((value) => value !== '');
  const newEntries = [
    ...(commands.can.create && inputs.drive?.new_menu?.create_folder !== false ? [{ id: 'folder', label: 'Nouveau dossier', icon: 'folder', onSelect: () => { setRenaming(null); setCreating(true); }, ...(may.create ? {} : { disabled: true, reason: NOT_PERMITTED }) }] : []),
    ...(inputs.drive?.new_menu?.upload === true || !drive ? (props.newEntries ?? []) : []),
  ];
  const chipMenus = (inputs.drive?.filter_chips ?? []).map((chip) => <FilterChip key={chip.field} client={client} chip={chip} value={chips[chip.field] ?? ''} onPick={(value) => setChips((before) => ({ ...before, [chip.field]: value }))} />);
  const sortMenu = sortOptions.length > 0 ? <ChipMenu label="Trier" options={sortOptions} selected={shortcut?.sort ?? sort ?? ''} onPick={(id) => setSort(id === '' ? undefined : id)} /> : null;
  const scopeChoice = spec.search_scope?.choice !== false && folderId !== null && !everywhere ? (
    <button type="button" className="ay-folders__chip" aria-pressed={scope === 'everywhere'} onClick={() => setScope(scope === 'folder' ? 'everywhere' : 'folder')}>
      Portée : <strong>{scope === 'folder' ? 'Ce dossier' : 'Partout'}</strong>
    </button>
  ) : null;
  const switcher = mode === undefined ? null : <ViewSwitch modes={modes} current={mode.id} onPick={setModeId} />;
  const viewProps = {
    folders: title !== undefined && q === '' ? [] : folders,
    items,
    presenter: { ...presenter, menu: itemMenu },
    columns,
    selection: inputs.bulk_commands === undefined && props.extraActions === undefined ? undefined : selection,
    clickSelects: inputs.selection?.click_selects === true,
    openKey: info?.kind === 'item' && infoOpen ? itemKey(presenter.id(info.item)) : null,
    itemsLabel: labels.items,
    folderCount: (folder: FolderEntry) => countOf(folder.item_count ?? 0, labels.one, labels.many),
    folderMenu,
    onOpenFolder: (id: string) => props.onNavigate(id),
    onOpenItem: openItem,
    renaming,
    onRename: async (folder: FolderEntry, name: string) => {
      const ok = await run(() => commands.rename(folder, name), `Dossier renommé en « ${name} ».`);
      if (ok) setRenaming(null);
      return ok;
    },
    onCancelRename: () => setRenaming(null),
    creating,
    onCreate: async (name: string) => {
      const ok = await run(() => commands.create(name, folderId), `Dossier « ${name} » créé.`);
      if (ok) setCreating(false);
      return ok;
    },
    onCancelCreate: () => setCreating(false),
    dragFrom,
    dropTarget,
    ...(props.trailing === undefined ? {} : { trailing: props.trailing }),
  };
  const statusLine = listing === null ? '' : `${countOf(total, labels.one, labels.many)}${q !== '' ? ` pour « ${q} »` : title !== undefined ? ` · ${title}` : ` dans « ${current?.name ?? labels.root} »`}${folders.length > 0 && q === '' && title === undefined ? ` · ${countOf(folders.length, 'sous-dossier', 'sous-dossiers')}` : ''}`;
  const tools = drive ? (
    <div className="ay-drive__tools">
      <div className="ay-drive__head">
        <span className="ay-folders__spacer" />
        {switcher}
        {props.info !== undefined && (
          <button type="button" className="ay-drive__info" aria-label="Afficher les informations" aria-pressed={infoOpen} onClick={() => { if (!infoOpen && info === null) { const single = pickedItems.length === 1 && pickedFolders.length === 0 ? pickedItems[0] : undefined; setInfo(single !== undefined ? { kind: 'item', item: single } : current !== null ? { kind: 'folder', folder: current, place: placeOf(current) } : null); } setInfoOpen(!infoOpen); }}>
            <Icon name="info" size={18} />
          </button>
        )}
      </div>
      <div className="ay-folders__chips" role="group" aria-label="Filtres">{chipMenus}{sortMenu}{scopeChoice}</div>
    </div>
  ) : (
    <div className="ay-folders__toolbar" role="toolbar" aria-label="Outils de la liste">
      <SearchBox value={draft} onChange={setDraft} placeholder={labels.search} />
      {scopeChoice}
      {chipMenus}
      {sortMenu}
      <span className="ay-folders__spacer" />
      {switcher}
    </div>
  );
  const bar = base.count > 0 ? <SelectionToolbar label={selectionLabel} everything={canSelectAll ? { label: `Sélectionner les ${total.toLocaleString('fr')} ${labels.many}${where}`, onSelect: base.selectAll } : undefined} actions={toolbarActions} onClear={base.clear} /> : tools;
  const body = failure !== null ? (
    <div role="alert" className="ay-folders__failure"><span>{failure}</span><button type="button" className="ay-button ay-button--secondary" onClick={refresh}>Réessayer</button></div>
  ) : listing === null ? <Loading /> : (
    <div className={`ay-folders__zone${dropping ? ' ay-folders__zone--active' : ''}`} {...fileZone}>
      {items.length === 0 && viewProps.folders.length === 0 && !creating ? props.empty?.(searching) : mode?.kind === 'table' ? <FolderTable {...viewProps} /> : <FolderGrid {...viewProps} />}
      {dropping && (
        <div className="ay-folders__veil" aria-hidden="true">
          <Icon name="upload" size={30} />
          <strong>Déposez vos {labels.many} ici</strong>
          <span>{current === null ? labels.root : `dans « ${current.name} »`}</span>
        </div>
      )}
    </div>
  );
  const panel = infoOpen && info !== null && props.info !== undefined ? props.info(info, { close: () => setInfoOpen(false), refresh, openFolder: (id) => props.onNavigate(id), rename: (folder) => { if (folder.id === current?.id) props.onNavigate(parentOf(folder)); setRenaming(folder.id); }, move: (item) => setMoving({ label: presenter.title(item), folders: [], items: [item] }) }) : null;
  // Arborescence : le nœud d'un dossier vu dans la liste garde ses nombres ; sinon, l'essentiel.
  const entryOf = (node: FolderNode): FolderEntry => folders.find((entry) => entry.id === node.id) ?? { id: node.id, name: node.name, locked: node.locked, readonly: false, raw: { parent_id: node.parent_id } };
  const treeProps = {
    folders: tree,
    current: folderId,
    rootLabel: labels.root,
    onOpen: (id: string | null) => { setShortcutId(null); props.onNavigate(id); },
    dropTarget,
    count: (id: string) => folders.find((entry) => entry.id === id)?.item_count,
    menu: (node: FolderNode) => folderMenu(entryOf(node)),
    ...(commands.can.create ? { onCreate: () => { setRenaming(null); setCreating(true); }, onCreateIn: (node: FolderNode) => { setRenaming(null); setShortcutId(null); props.onNavigate(node.id); setCreating(true); }, ...(may.create ? {} : { createDenied: NOT_PERMITTED }) } : {}),
  };
  const pageActions = (props.newEntries ?? []).map((entry) => (
    <button key={entry.id} type="button" className="ay-button" aria-disabled={entry.disabled === true ? true : undefined} title={entry.reason} onClick={() => entry.disabled !== true && entry.onSelect()}>
      <Icon name={entry.icon} size={15} /> {entry.label}
      {entry.disabled === true && entry.reason !== undefined && <span className="ay-visually-hidden"> — {entry.reason}</span>}
    </button>
  ));
  return (
    <div className="ay-folders-page">
    <FolderBreadcrumb trail={props.trail ?? []} rootLabel={labels.root} path={crumbs} {...(title === undefined ? {} : { title })} onOpen={(id) => { setShortcutId(null); props.onNavigate(id); }} dropTarget={dropTarget} />
    <PageHeader
      title={title ?? current?.name ?? labels.root}
      {...(title === undefined && props.subtitle?.(current) !== undefined ? { subtitle: props.subtitle(current) } : {})}
      {...(current?.locked === true && title === undefined ? { badge: <span className="ay-folders__system" title="Dossier système : ni renommé, ni déplacé, ni archivé"><Icon name="lock" size={12} /> Dossier système</span> } : {})}
      {...(pageActions.length === 0 ? {} : { actions: <>{pageActions}</> })}
      {...(current === null || title !== undefined ? {} : { more: folderMenu(current) })}
    />
    <div className={`ay-folders ay-folders--${drive ? 'drive' : 'tree'}`} onKeyDown={inputs.selection?.keyboard === false ? undefined : selectionKeys(base, () => order)}>
      {drive ? (
        <aside className="ay-drive__aside" aria-label={labels.root}>
          <NewMenu entries={newEntries} />
          <Shortcuts items={shortcuts.map((entry) => ({ id: entry.shortcut_id, label: text(entry.label), ...(entry.icon === undefined ? {} : { icon: entry.icon }) }))} current={shortcutId ?? (folderId === null ? shortcuts[0]?.shortcut_id ?? null : null)} onPick={(id) => { const picked = shortcuts.find((entry) => entry.shortcut_id === id)!; if (picked.workspace_ref !== undefined) { props.onNavigateWorkspace?.(picked.workspace_ref); return; } if (picked.filter === undefined && picked.sort === undefined && picked.everywhere !== true) { setShortcutId(null); props.onNavigate(null); return; } setShortcutId(id); }} />
          {/* Comme « Mon Drive » : l'arborescence des dossiers sous le raccourci de la racine. */}
          <FolderTree {...treeProps} hideRoot />
        </aside>
      ) : (
        <FolderTree {...treeProps} />
      )}
      <div className="ay-folders__main">
        {drive && <SearchBox value={draft} onChange={setDraft} placeholder={labels.search} pill />}
        {bar}
        {props.notice}
        {body}
        {listing !== null && failure === null && <span role="status" className="ay-folders__count">{statusLine}</span>}
      </div>
      {panel}
      {moving !== null && (
        <MoveToFolderDialog
          load={() => source.allFolders()}
          rootLabel={`Racine — ${labels.root}`}
          subtitle={`${labels.root} · ranger ne change aucun lien ni aucune version`}
          label={moving.label}
          from={folderId}
          exclude={moving.folders.map((folder) => folder.id)}
          onClose={() => setMoving(null)}
          onMove={(target) => { const job = moving; setMoving(null); void moveTo(job, target); }}
        />
      )}
      {asking !== null && (
        <Dialog title={asking.title} subtitle={labels.root} open size="sm" onClose={() => setAsking(null)} footer={<DialogActions destructive={{ label: asking.label, onClick: () => { const pending = asking; setAsking(null); pending.run(); } }} cancel={{ label: 'Annuler', onClick: () => setAsking(null) }} />}>
          <p className="ay-selbar__confirm">{asking.message}</p>
        </Dialog>
      )}
      {confirming !== null && (
        <Dialog title={`Archiver le dossier « ${confirming.folder.name} » ?`} subtitle={labels.root} open size="sm" onClose={() => setConfirming(null)} footer={<DialogActions destructive={{ label: 'Archiver le dossier et son contenu', onClick: () => { const { folder, count: inside } = confirming; setConfirming(null); void run(() => commands.archive(folder, { recursive: true, expected_count: inside }), `Dossier « ${folder.name} » et ${countOf(inside, 'élément', 'éléments')} archivés.`); } }} cancel={{ label: 'Annuler', onClick: () => setConfirming(null) }} />}>
          <div className="ay-folders__warning" role="alert"><Icon name="alert" size={18} /><span>Il contient <strong>{countOf(confirming.count, 'élément', 'éléments')}</strong>. Ils seront archivés avec lui ; les liens vers ces éléments restent valides.</span></div>
        </Dialog>
      )}
    </div>
    </div>
  );
}

function SearchBox({ value, onChange, placeholder, pill = false }: { value: string; onChange: (value: string) => void; placeholder: string; pill?: boolean }) {
  return (
    <label className={`ay-folders__search${pill ? ' ay-folders__search--pill' : ''}`}>
      <Icon name="search" size={pill ? 17 : 14} />
      <input type="search" value={value} placeholder={placeholder} aria-label={placeholder} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

type ChipSpec = NonNullable<NonNullable<ReturnType<typeof collectionInputs>['drive']>['filter_chips']>[number];

/** Puce de filtre déclarée : options fixes, ou lues par une requête déclarée (vocabulaire d'étiquettes). */
function FilterChip({ client, chip, value, onPick }: { client: ApiClient; chip: ChipSpec; value: string; onPick: (value: string) => void }) {
  const [loaded, setLoaded] = useState<Array<{ id: string; label: string }>>([]);
  const declared = chip.options_query;
  const key = JSON.stringify(declared ?? null);
  useEffect(() => {
    if (declared === undefined) return;
    let alive = true;
    client
      .query<Record<string, unknown>>(declared.query_name, { ...(declared.parameters ?? {}), limit: '200' })
      .then((response) => {
        if (!alive) return;
        const list = (response.data[declared.list_key] ?? []) as Array<Record<string, unknown>>;
        setLoaded(list.map((entry) => ({ id: String(entry[declared.value_key]), label: declared.count_key === undefined ? String(entry[declared.value_key]) : `${String(entry[declared.value_key])} (${String(entry[declared.count_key] ?? 0)})` })).sort((a, b) => a.id.localeCompare(b.id, 'fr')));
      })
      .catch(() => alive && setLoaded([]));
    return () => {
      alive = false;
    };
    // La clé résume la requête déclarée.
  }, [client, key]);
  const options = chip.options !== undefined ? chip.options.map((option) => ({ id: option.value, label: text(option.label) })) : loaded;
  return <ChipMenu label={text(chip.label)} options={options} selected={value} onPick={onPick} />;
}
